import { clsx } from 'clsx';
import { format, parseISO } from 'date-fns';
import React from 'react';
import { twMerge } from 'tailwind-merge';
import { AssignmentList, SourceTypeEnum } from '../../../../../js/api';
import { useCurrencyFormatter } from '../../../../../js/app/hooks/use-currency-formatter';
import { StatusPill } from '../../../components/StatusPill';
import { WorkflowScopes } from '../../../constants/workflows';
import { SOURCE_TYPE_ORGANIZATION_BADGE_STYLE } from '../../../utils/dataStyles';

const DATE_FORMAT = 'EEE LLL d, yyyy';

export const AssignmentCard: React.FC<{
  assignment: AssignmentList;
  isSelected?: boolean;
}> = ({ assignment, isSelected = false }) => {
  const dateRange = [
    assignment.start_on ? format(parseISO(assignment.start_on), DATE_FORMAT) : null,
    assignment.end_on ? format(parseISO(assignment.end_on), DATE_FORMAT) : null,
  ]
    .filter(Boolean)
    .join(' - ');
  const { formatCurrencyPostfix } = useCurrencyFormatter({ postfix: '', placeholder: '-' });
  const shift_count =
    assignment.active_shift_count > 0
      ? assignment.active_shift_count
      : assignment.removed_shift_count || 0;
  return (
    <div
      className={twMerge(
        clsx(
          'tw:h-full tw:bg-white tw:rounded-lg tw:shadow-sm tw:border tw:border-zinc-200 tw:border-l-brand-black-2 tw:border-l-6 tw:hover:shadow-md tw:hover:bg-muted tw:transition-shadow tw:cursor-pointer',
          isSelected && 'tw:!bg-muted tw:!shadow-md'
        )
      )}
    >
      <div className="tw:p-3">
        <div className="tw:flex tw:justify-between tw:items-start tw:mb-3">
          <div>
            <i className="far fa-lg fa-calendar-lines-pen tw:text-zinc-700 tw:mr-1" />
            <span className="tw:text-sm tw:text-zinc-700">{assignment.code}</span>
          </div>
          <StatusPill
            scope={WorkflowScopes.Assignment}
            status={assignment.current_status || 'Unknown'}
          />
        </div>

        <div className="tw:flex tw:flex-col tw:gap-0.5 tw:mb-2 tw:min-w-0">
          {(() => {
            const facilities =
              (assignment as any).metadata?.shift_facilities?.length > 0
                ? (assignment as any).metadata.shift_facilities
                : [
                    {
                      facility_id: assignment.facility.id,
                      facility_name: assignment.facility.name,
                      facility_city: assignment.facility.city,
                      facility_state: assignment.facility.state,
                    },
                  ];
            return facilities.map((f: any) => (
              <div key={f.facility_id} className="tw:flex tw:items-center tw:gap-1 tw:min-w-0">
                <i className="fas fa-fw fa-hospital tw:text-brand-green-0 tw:shrink-0" />
                <span className="tw:font-medium tw:text-brand-green-0" title={f.facility_name}>
                  {f.facility_name}
                </span>
              </div>
            ));
          })()}
        </div>

        <div className="tw:flex tw:flex-col tw:text-sm tw:text-zinc-500 tw:mb-1 tw:gap-y-1">
          <div className="tw:flex tw:items-center tw:gap-1">
            <i className="fas fa-fw fa-map-marker-alt tw:text-zinc-500" />
            <span>
              {(() => {
                const facilities =
                  (assignment as any).metadata?.shift_facilities?.length > 0
                    ? (assignment as any).metadata.shift_facilities
                    : [
                        {
                          facility_city: assignment.facility.city,
                          facility_state: assignment.facility.state,
                        },
                      ];
                const firstLoc = facilities[0];
                const locations = new Map<string, string>();
                for (const f of facilities) {
                  if (f.facility_city && f.facility_state) {
                    const key = `${f.facility_city}|${f.facility_state}`;
                    if (!locations.has(key)) {
                      locations.set(key, `${f.facility_city}, ${f.facility_state}`);
                    }
                  }
                }
                if (locations.size === 0 && firstLoc?.facility_city && firstLoc?.facility_state) {
                  return `${firstLoc.facility_city}, ${firstLoc.facility_state}`;
                }
                return Array.from(locations.values()).join(' · ');
              })()}
            </span>
          </div>
          <div className="tw:text-sm tw:text-zinc-500 tw:gap-1">
            <i className="far fa-fw fa-calendar tw:mr-1" />
            {dateRange}
          </div>
          <div className="tw:text-sm tw:text-zinc-500 tw:gap-1">
            {Number(assignment.total_amount) > 0 && (
              <>
                <i className="far fa-fw fa-sack-dollar tw:mr-1" />
                {formatCurrencyPostfix(assignment.total_amount)}
                <span className="tw:px-1">&bull;</span>
              </>
            )}
            {shift_count} {shift_count === 1 ? 'shift' : 'shifts'}
            {assignment.source_type === SourceTypeEnum.ORGANIZATION && (
              <span className={clsx('tw:ml-3', SOURCE_TYPE_ORGANIZATION_BADGE_STYLE)}>1099</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
